// The label form, shared between two pages with different transports:
//   Tools → Print a label   (standalone manual label, not tied to any order)
//   Order → Custom label    (prefilled from the order, attaches to the order)
// The form owns the whole print interaction (popup window opened inside the
// click, Zebra detection, deliverLabel); the page owns fetching `opts` and the
// actual POST via `onSubmit`, so `initial` and `opts` always arrive together
// and a prefill can never race the options load. (Claude, 2026-08-30)
import React, { useEffect, useRef, useState } from 'react';
import { authFetch, API } from './api';
import { detectZebraIfEnabled, deliverLabel } from './labelPrint';

// Every country DPD's locker API serves pickup points for (verified by sweep
// 2026-08-04). This list was the bug twice over: CZ was missing when a WGR order
// needed a Czech parcelshop, then NL when a Dutch one did. Nordics/Baltics first
// because that is the daily traffic, then the rest alphabetically. Keep this in
// step with DEFAULT_COUNTRIES in main.py's _sync_all_dpd_pickup_points. (Claude)
export const COUNTRIES = ['EE', 'FI', 'LV', 'LT', 'SE', 'NO', 'DK',
                          'AT', 'BE', 'BG', 'CH', 'CZ', 'DE', 'ES', 'FR', 'GB',
                          'GR', 'HR', 'HU', 'IE', 'IT', 'LU', 'NL', 'PL', 'PT',
                          'RO', 'SI', 'SK'];

export interface Sender {
  id: number; name: string; city?: string; postal_code?: string;
  country?: string; address1?: string; phone?: string | null;
  webshop_id?: string; store_name?: string | null;
}
export interface OptionalField { key: string; label: string; placeholder?: string; }
export interface DeliveryType { key: string; label: string; }
export interface Carrier {
  code: string; name: string;
  optional_fields?: OptionalField[];
  delivery_types?: DeliveryType[];
}
export interface Locker {
  id: string; name: string; address?: string; city?: string; zip?: string;
  // False once the carrier stops returning the point. Kept selectable for a
  // retention window so an order placed before it closed can still be printed --
  // but shown as "no longer listed" so nobody picks it for a new parcel.
  is_active?: boolean; last_seen_at?: string | null;
  // Set by the custom-label defaults when the order's point is not in the
  // synced list at all (dead or never synced) — the headline rescue case.
  missing?: boolean;
}
export interface PacketaService {
  method_name: string; delivery_shape: string;
  country: string | null; service_id: string | null;
  // separateHouseNumber on the carrier Packeta resells: the number must be sent
  // in its own field, so the form has to offer one rather than let the backend
  // guess the split out of the street line.
  requires_house_number?: boolean;
}
export interface LabelFormOpts {
  senders: Sender[]; carriers: Carrier[]; default_weight_kg: number;
  packeta_services?: PacketaService[];
}
export interface Rcv {
  name: string; address1: string; address2: string; postal_code: string;
  city: string; country: string; contact_name: string; phone: string; email: string;
  // Only sent (and only shown) for carriers that demand it apart from the street.
  house_number?: string;
}
export interface LabelFormInitial {
  senderId?: number | null; carrier?: string | null; dtype?: string;
  rcv?: Partial<Rcv>; reference?: string; weightKg?: number;
  pp?: Locker | null; packetaMethod?: string;
  // Cash on delivery. Only the order-scoped page passes these — the Tools form
  // has no order behind it, so it never shows the field.
  paymentKind?: string | null; codAmount?: number | null; currency?: string | null;
  // Contact repair (docs/CONTACT-REPAIR.md): per field, the shape verdict for the
  // order's value and the customer's own working values as one-tap chips. Order page only.
  fieldHints?: Record<string, FieldHint | undefined> | null;
}
export interface FieldHint {
  given?: string; shape?: string; message?: string | null;
  suggestions?: { value: string; label: string }[];
}

/** The verdict line (until the value is changed) and the chips, for one field. */
function Hints({ hint, value, onPick }: { hint?: FieldHint | null; value: string; onPick: (v: string) => void }) {
  if (!hint) return null;
  const untouched = value.trim() === (hint.given || '').trim();
  const sugg = (hint.suggestions || []).filter(s => s.value !== value.trim());
  if (!(hint.message && untouched) && sugg.length === 0) return null;
  return (
    <>
      {hint.message && untouched && <p className="text-amber-400 text-xs mt-1">{hint.message}</p>}
      {sugg.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {sugg.map(s => (
            <button key={s.value} type="button" onClick={() => onPick(s.value)} title={s.label}
              className="px-2 py-0.5 rounded-full text-xs border bg-gray-800 border-gray-700 text-gray-300 hover:text-white">
              {s.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

const inp = 'w-full bg-gray-900 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500';
const lbl = 'text-gray-400 text-xs uppercase tracking-wider';

export default function LabelForm({ opts, initial, onSubmit, onDone, submitLabel }: {
  opts: LabelFormOpts;
  initial?: LabelFormInitial;
  // Does the POST (page-specific endpoint) and returns the raw Response.
  onSubmit: (body: Record<string, any>) => Promise<Response>;
  onDone?: (data: any) => void;
  submitLabel?: string;
}) {
  const [senderId, setSenderId] = useState<number | ''>(initial?.senderId ?? '');
  const [carrier, setCarrier] = useState(initial?.carrier ?? '');
  const [dtype, setDtype] = useState<string>(initial?.dtype ?? 'hd');
  const [rcv, setRcv] = useState<Rcv>({
    name: '', address1: '', address2: '', postal_code: '', city: '', country: 'EE',
    contact_name: '', phone: '', email: '', house_number: '',
    ...(initial?.rcv || {}),
  });
  // What the carriers' own synced pickup points call this postcode. A hint, never
  // a block — see /api/labels/place-check.
  const [place, setPlace] = useState<{ known: boolean; cities: string[];
                                       matches?: boolean; reordered?: boolean } | null>(null);
  const [reference, setReference] = useState(initial?.reference ?? '');
  const [optional, setOptional] = useState<Record<string, string>>({});
  const [weight, setWeight] = useState(String(initial?.weightKg ?? opts.default_weight_kg ?? ''));
  const [pp, setPp] = useState<Locker | null>(initial?.pp ?? null);
  const [pkMethod, setPkMethod] = useState(initial?.packetaMethod ?? '');
  // Cash on delivery — what the courier collects at the door. Editable, because
  // the order total is not always what should be collected (Fernando, 2026-09-14).
  const isCod = (initial?.paymentKind || '') === 'cod';
  const orderCod = Number(initial?.codAmount ?? 0);
  const [cod, setCod] = useState(isCod ? String(orderCod) : '');
  const [ppQuery, setPpQuery] = useState('');
  const [ppResults, setPpResults] = useState<Locker[]>([]);
  const [ppTotal, setPpTotal] = useState<{ total: number; truncated: boolean }>({ total: 0, truncated: false });
  const [printing, setPrinting] = useState(false);
  const [err, setErr] = useState('');

  const isPacketa = carrier === 'packeta';
  const pkRows = opts.packeta_services || [];
  const pkNames = Array.from(new Set(pkRows.map(r => r.method_name)));
  // Exact-country row first, then the any-country row — the same precedence the
  // backend mapping lookup uses.
  const pkSel = pkMethod
    ? (pkRows.find(r => r.method_name === pkMethod && r.country === rcv.country)
       || pkRows.find(r => r.method_name === pkMethod && !r.country)
       || pkRows.find(r => r.method_name === pkMethod) || null)
    : null;
  const pkNeedsPp = !!pkSel && pkSel.delivery_shape !== 'home';
  // Whether the pickup-point picker is on screen at all.
  const wantsPp = isPacketa ? pkNeedsPp : dtype === 'pp';
  // A street address only goes to the carrier on home delivery, so the separate
  // house number is asked for there and nowhere else.
  const needsHouse = isPacketa && !!pkSel?.requires_house_number && !pkNeedsPp;

  // Pickup-point search (carrier + destination country), debounced.
  useEffect(() => {
    if (!wantsPp || !carrier) { setPpResults([]); return; }
    const t = setTimeout(async () => {
      // Packeta: only the points the chosen service delivers to (its own network, or
      // the resold carrier's) — the list used to mix every network
      const pk = isPacketa && pkSel
        ? `&packeta_shape=${encodeURIComponent(pkSel.delivery_shape || '')}&packeta_service=${encodeURIComponent(pkSel.service_id || '')}`
        : '';
      const r = await authFetch(`${API}/manual-labels/lockers?carrier=${carrier}&country=${rcv.country}&q=${encodeURIComponent(ppQuery)}${pk}`);
      if (r.ok) {
        const d = await r.json();
        setPpResults(d.lockers || []);
        setPpTotal({ total: d.total ?? (d.lockers || []).length, truncated: !!d.truncated });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [ppQuery, carrier, wantsPp, rcv.country, isPacketa, pkSel?.delivery_shape, pkSel?.service_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Postcode ↔ town, checked against the carriers' own synced point lists. Free
  // and local; catches the class of error that only surfaces at the carrier
  // AFTER a packet exists (HR10039: "Ivanićko graberje" for 10313 "Graberje
  // Ivaničko"). Debounced, and silent when the postcode is not in any list.
  useEffect(() => {
    const zip = (rcv.postal_code || '').trim();
    if (!zip || !rcv.country) { setPlace(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await authFetch(`${API}/labels/place-check?country=${rcv.country}`
          + `&postal_code=${encodeURIComponent(zip)}&city=${encodeURIComponent(rcv.city || '')}`);
        setPlace(r.ok ? await r.json() : null);
      } catch { setPlace(null); }
    }, 350);
    return () => clearTimeout(t);
  }, [rcv.postal_code, rcv.city, rcv.country]);

  const selCarrier = opts.carriers.find(c => c.code === carrier);
  const optFields = selCarrier?.optional_fields || [];
  // Delivery types come from the backend per carrier (derived from that carrier's
  // label recipes), so only DPD offers B2B and nobody can pick a type that 400s.
  const dTypes: DeliveryType[] = selCarrier?.delivery_types
    || [{ key: 'hd', label: '🚚 Home / courier' },
        { key: 'pp', label: '📦 Pickup / parcel machine' }];

  // Resets on change, NOT on mount — a prefilled point/type must survive the
  // first render. Change-detection via a ref seeded with the initial values,
  // rather than a didMount flag: StrictMode re-runs mount effects and a flag
  // ref survives that, so the second pass would clobber the prefill; comparing
  // against the previous value cannot. (Claude, 2026-08-30)
  const prev = useRef({
    carrier: initial?.carrier ?? '', dtype: initial?.dtype ?? 'hd',
    // Must equal the rcv state initializer's country, whatever path produced it.
    country: initial?.rcv?.country ?? 'EE',
    pkMethod: initial?.packetaMethod ?? '',
  });
  useEffect(() => {
    const p = prev.current;
    // The chosen pickup point belongs to (carrier, type, country, service).
    if (p.carrier !== carrier || p.dtype !== dtype || p.country !== rcv.country
        || p.pkMethod !== pkMethod) setPp(null);
    // Optional fields are carrier-specific — clear their values when carrier changes.
    if (p.carrier !== carrier) {
      setOptional({});
      // Switching to a carrier that lacks the selected type (e.g. DPD B2B -> Posti)
      // must not leave an unprintable selection behind.
      if (carrier !== 'packeta' && dTypes.length && !dTypes.some(d => d.key === dtype)) {
        setDtype(dTypes[0].key);
      }
    }
    prev.current = { carrier, dtype, country: rcv.country, pkMethod };
  }, [carrier, dtype, rcv.country, pkMethod]);  // eslint-disable-line react-hooks/exhaustive-deps

  const setR = (k: keyof Rcv, v: string) => setRcv(p => ({ ...p, [k]: v }));

  const doPrint = async () => {
    setErr('');
    if (!senderId) { setErr('Choose a sender'); return; }
    if (!carrier) { setErr('Choose a carrier'); return; }
    if (!rcv.name.trim()) { setErr('Receiver name is required'); return; }
    if (isPacketa && !pkMethod) { setErr('Pick a Packeta service'); return; }
    if (wantsPp && !pp) { setErr('Pick a pickup point'); return; }
    if (needsHouse && !(rcv.house_number || '').trim()) {
      setErr(`${pkSel?.method_name} needs the house number in its own field`); return;
    }
    if (isCod) {
      const c = parseFloat(cod);
      if (!(c > 0)) { setErr('A cash-on-delivery parcel must collect an amount above zero'); return; }
      // Changing what the courier collects is money, so it is confirmed and,
      // on the backend, written onto the order.
      if (Math.round(c * 100) !== Math.round(orderCod * 100) &&
          !window.confirm(`The courier will collect ${c.toFixed(2)} ${initial?.currency || ''}.\n`
            + `The order says ${orderCod.toFixed(2)} ${initial?.currency || ''}.\n\n`
            + `Buy the label with the amount you typed?`)) return;
    }
    const win = window.open('', '_blank');
    if (win) win.document.write('<p style="font-family:sans-serif;padding:2rem">Generating label…</p>');
    setPrinting(true);
    try {
      const zebra = await detectZebraIfEnabled();
      const res = await onSubmit({
        carrier, delivery_type: dtype, sender_id: senderId,
        pickup_point_id: (wantsPp && pp?.id) || '', reference,
        packeta_method_name: isPacketa ? pkMethod : '',
        contents: optional.contents || '', delivery_instruction: optional.delivery_instruction || '',
        weight_kg: weight === '' ? undefined : parseFloat(weight),
        // Ignored by the backend unless the order really is cash on delivery.
        cod_amount: isCod ? parseFloat(cod) : undefined,
        // The house number only travels for the carriers that ask for it; sending
        // it otherwise would duplicate the number the street line already carries.
        receiver: needsHouse ? rcv : { ...rcv, house_number: '' },
        format: zebra ? 'zpl' : 'pdf',
      });
      const data = await res.json();
      if (!res.ok) { if (win) win.close(); setErr(data.detail || 'Print failed'); return; }
      await deliverLabel(win, data, zebra);
      onDone?.(data);
    } catch {
      if (win) win.close(); setErr('Print failed');
    } finally { setPrinting(false); }
  };

  // Show the address, not just the name: two senders may share a name (Motley Denim had
  // the Tallinn warehouse and a Bring Sweden row, both "Motley Denim Europe OÜ" — the
  // Swedish one was picked for a DPD label on 8.10 and deleted 13.10: Bring prints its
  // Swedish sender from the Bring account, nothing used the row). (Claude, 2026-07-26 / 10-13)
  const senderLabel = (s: Sender) => {
    const place = [s.city, s.country].filter(Boolean).join(', ');
    return `${s.name}${place ? ` — ${place}` : ''}${s.store_name ? ` (${s.store_name})` : ''}`;
  };

  return (
    <div className="space-y-6">
      {/* Sender + carrier */}
      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5 space-y-4">
        <div className="text-gray-400 text-xs uppercase tracking-widest font-semibold">Sender & carrier</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={lbl}>Sender</label>
            <select value={senderId} onChange={e => setSenderId(e.target.value ? Number(e.target.value) : '')} className={inp + ' mt-1'}>
              <option value="">Choose sender…</option>
              {opts.senders.map(s => <option key={s.id} value={s.id}>{senderLabel(s)}</option>)}
            </select>
            {/* DPD refuses a sender without phone or city (2026-10-08) — say it before Print, not after */}
            {(() => {
              const s = opts.senders.find(x => x.id === senderId);
              if (!s || carrier !== 'dpd_baltics') return null;
              const miss = [!s.city && 'city', !s.phone && 'phone'].filter(Boolean);
              return miss.length ? <p className="text-xs text-amber-300 mt-1">This sender has no {miss.join(' or ')} — DPD needs it (unless the contract has a fallback phone). Settings → Shipping → Senders.</p> : null;
            })()}
          </div>
          <div>
            <label className={lbl}>Carrier</label>
            <select value={carrier} onChange={e => setCarrier(e.target.value)} className={inp + ' mt-1'}>
              <option value="">Choose carrier…</option>
              {opts.carriers.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </div>
        </div>
        {isPacketa ? (
          <div>
            <label className={lbl}>Packeta service <span className="text-gray-600">(the store's mapped services — this decides the carrier Packeta resells)</span></label>
            <select value={pkMethod} onChange={e => setPkMethod(e.target.value)} className={inp + ' mt-1'}>
              <option value="">Choose service…</option>
              {pkNames.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        ) : (
          <div>
            <label className={lbl}>Delivery type</label>
            <div className="mt-1 flex gap-2">
              {dTypes.map(({ key: v, label: t }) => (
                <button key={v} onClick={() => setDtype(v)}
                  className={`px-4 py-2 text-sm rounded-lg border transition-colors ${
                    dtype === v ? 'bg-blue-600 border-blue-500 text-white' : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white'
                  }`}>{t}</button>
              ))}
            </div>
          </div>
        )}
        {wantsPp && (
          <div>
            <label className={lbl}>Pickup point <span className="text-gray-600">(search by name, city, postcode, street or code)</span></label>
            {pp ? (
              <div className="mt-1 flex items-center justify-between bg-gray-900 border border-gray-700 rounded-lg px-3 py-2">
                <span className="text-sm text-white">
                  {pp.name}<span className="text-gray-500"> · {pp.zip} {pp.city}</span>
                  {pp.missing ? (
                    <span className="ml-2 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-red-900/50 text-red-400">
                      not in the synced list — pick another
                    </span>
                  ) : pp.is_active === false && (
                    <span className="ml-2 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-amber-900/50 text-amber-400">
                      no longer listed by carrier
                    </span>
                  )}
                </span>
                <button onClick={() => setPp(null)} className="text-xs text-gray-400 hover:text-red-400">change</button>
              </div>
            ) : (
              <>
                <input value={ppQuery} onChange={e => setPpQuery(e.target.value)} placeholder="Start typing…"
                  className={inp + ' mt-1'} disabled={!carrier} />
                {ppResults.length > 0 && (
                  <div className="mt-1 max-h-52 overflow-y-auto border border-gray-800 rounded-lg divide-y divide-gray-800">
                    {ppResults.map(l => (
                      <button key={l.id} onClick={() => { setPp(l); setPpQuery(''); }}
                        className="w-full text-left px-3 py-2 hover:bg-gray-800 text-sm">
                        <span className={l.is_active === false ? 'text-gray-400' : 'text-white'}>{l.name}</span>
                        <span className="text-gray-500 text-xs"> · {l.zip} {l.city}{l.address ? ` · ${l.address}` : ''}</span>
                        {l.is_active === false && (
                          <span className="ml-2 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-amber-900/50 text-amber-400">
                            no longer listed
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                {/* A capped list must never look like the whole list. */}
                {ppTotal.truncated && (
                  <p className="text-amber-500/80 text-xs mt-1">
                    Showing {ppResults.length} of {ppTotal.total} matches — narrow the search (a postcode is most precise).
                  </p>
                )}
                {!carrier && <p className="text-gray-600 text-xs mt-1">Choose a carrier first.</p>}
              </>
            )}
          </div>
        )}
      </div>

      {/* Receiver */}
      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5 space-y-4">
        <div className="text-gray-400 text-xs uppercase tracking-widest font-semibold">Receiver</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className={lbl}>Name * <span className="text-gray-600">(company, for B2B)</span></label>
            <input value={rcv.name} onChange={e => setR('name', e.target.value)} className={inp + ' mt-1'} />
          </div>
          <div>
            <label className={lbl}>Address 1</label>
            <input value={rcv.address1} onChange={e => setR('address1', e.target.value)} className={inp + ' mt-1'} />
            <Hints hint={initial?.fieldHints?.street_address} value={rcv.address1} onPick={v => setR('address1', v)} />
          </div>
          <div>
            <label className={lbl}>Address 2</label>
            <input value={rcv.address2} onChange={e => setR('address2', e.target.value)} className={inp + ' mt-1'} />
          </div>
          {/* This carrier stores the number in its own field and looks the street
              name up without it. Shown only for those carriers, prefilled with the
              number split off the address line — correct it here rather than
              rewriting the address line, which is what breaks the split. */}
          {needsHouse && (
            <div className="col-span-2">
              <label className={lbl}>House number *{' '}
                <span className="text-gray-600">
                  {pkSel?.method_name} sends the number separately — the street name is looked up without it
                </span>
              </label>
              <input value={rcv.house_number || ''} onChange={e => setR('house_number', e.target.value)}
                placeholder="e.g. 49a" className={inp + ' mt-1 max-w-[12rem]'} />
              <Hints hint={initial?.fieldHints?.house_number} value={rcv.house_number || ''} onPick={v => setR('house_number', v)} />
              {!!(rcv.house_number || '').trim() && (
                <p className="text-gray-500 text-xs mt-1">
                  Sending street “{(rcv.address1 || '').trim()
                    .replace(new RegExp('[\\s,]*' + (rcv.house_number || '').trim()
                      .replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\.?$', 'i'), '').trim() || (rcv.address1 || '').trim()}”
                  and house number “{(rcv.house_number || '').trim()}”.
                </p>
              )}
            </div>
          )}
          <div>
            <label className={lbl}>Postal code</label>
            <input value={rcv.postal_code} onChange={e => setR('postal_code', e.target.value)} className={inp + ' mt-1'} />
            <Hints hint={initial?.fieldHints?.postal_code} value={rcv.postal_code} onPick={v => setR('postal_code', v)} />
          </div>
          <div>
            <label className={lbl}>City</label>
            <input value={rcv.city} onChange={e => setR('city', e.target.value)} className={inp + ' mt-1'} />
            <Hints hint={initial?.fieldHints?.city} value={rcv.city} onPick={v => setR('city', v)} />
            {/* The carriers' own spelling for this postcode. Never blocks. */}
            {place?.known && !place.matches && (
              <p className="text-amber-400 text-xs mt-1">
                {place.reordered
                  ? 'Same words, the other way round — the carriers list '
                  : 'The carriers list '}
                {rcv.postal_code.trim()} as{' '}
                {place.cities.map((c, i) => (
                  <React.Fragment key={c}>
                    {i > 0 && ' / '}
                    <button onClick={() => setR('city', c)} className="underline hover:text-amber-300">{c}</button>
                  </React.Fragment>
                ))}
                . Their address table is matched on the name, so a different form is refused.
              </p>
            )}
          </div>
          <div>
            <label className={lbl}>Country</label>
            <select value={rcv.country} onChange={e => setR('country', e.target.value)} className={inp + ' mt-1'}>
              {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
          {/* Second name on the parcel: "Name" carries the company, this carries the
              one person there who accepts it. Goes to DPD receiverAddress.contactInfo
              (max 35). Posti/Omniva have no equivalent field, hence the hint.
              (Claude, 2026-08-04) */}
          <div>
            <label className={lbl}>Contact person <span className="text-gray-600">(DPD only)</span></label>
            <input value={rcv.contact_name} maxLength={35}
              onChange={e => setR('contact_name', e.target.value)}
              placeholder="Person at the company, max 35" className={inp + ' mt-1'} />
          </div>
          <div>
            <label className={lbl}>Phone</label>
            <input value={rcv.phone} onChange={e => setR('phone', e.target.value)} placeholder="+372…" className={inp + ' mt-1'} />
            <Hints hint={initial?.fieldHints?.phone} value={rcv.phone} onPick={v => setR('phone', v)} />
          </div>
          <div>
            <label className={lbl}>Email</label>
            <input value={rcv.email} onChange={e => setR('email', e.target.value)} className={inp + ' mt-1'} />
            <Hints hint={initial?.fieldHints?.email} value={rcv.email} onPick={v => setR('email', v)} />
          </div>
        </div>
      </div>

      {/* Shipment details */}
      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5 space-y-4">
        <div className="text-gray-400 text-xs uppercase tracking-widest font-semibold">Shipment</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={lbl}>Reference <span className="text-gray-600">(e.g. order number)</span></label>
            <input value={reference} onChange={e => setReference(e.target.value)} className={inp + ' mt-1'} />
          </div>
          <div>
            <label className={lbl}>Weight (kg)</label>
            <input type="number" step="0.1" min="0" value={weight} onChange={e => setWeight(e.target.value)} className={inp + ' mt-1'} />
          </div>
          {isCod && (
            <div>
              <label className={lbl}>Cash on delivery *{' '}
                <span className="text-gray-600">what the courier collects at the door</span>
              </label>
              <div className="flex items-center gap-2 mt-1">
                <input type="number" step="0.01" min="0" value={cod}
                  onChange={e => setCod(e.target.value)} className={inp + ' w-44'} />
                <span className="text-gray-400 text-sm">{initial?.currency || ''}</span>
              </div>
              <p className={`text-xs mt-1 ${
                Math.round(parseFloat(cod || '0') * 100) === Math.round(orderCod * 100)
                  ? 'text-gray-600' : 'text-amber-400'}`}>
                The order says {orderCod.toFixed(2)} {initial?.currency || ''}
                {Math.round(parseFloat(cod || '0') * 100) !== Math.round(orderCod * 100)
                  && ' — you will be asked to confirm the change'}
              </p>
            </div>
          )}
          {/* Carrier-specific optional fields — only shown for carriers whose API
              actually accepts them. Driven entirely by the backend registry. */}
          {optFields.map(f => (
            <div key={f.key}>
              <label className={lbl}>{f.label}</label>
              <input value={optional[f.key] || ''} placeholder={f.placeholder}
                onChange={e => setOptional(p => ({ ...p, [f.key]: e.target.value }))}
                className={inp + ' mt-1'} />
            </div>
          ))}
        </div>
      </div>

      {err && <div className="text-red-400 text-sm bg-red-900/20 border border-red-800/40 rounded-lg px-4 py-2">{err}</div>}

      <div className="flex justify-end">
        <button onClick={doPrint} disabled={printing}
          className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors">
          {printing ? 'Printing…' : (submitLabel || '🖨 Print label')}
        </button>
      </div>
    </div>
  );
}
